<template>
  <div class="configuration-item" id='row'>
    <div class="configuration-item__control">
      <CustomCheckbox
        color="cold-marine"
        :value="isSelected"
        size="s"
        @input="$emit('checked', website)"
      />
    </div>
    <div class='configuration-item__content'>
      <div class="configuration-item__content__info">
        <div class="main-info" @click="isOpen = !isOpen">
          <div class="site" :class="{ successful: !isVulnerable }">
            <v-menu
              bottom
              right
              offset-x
              :close-on-content-click='true'
              :nudge-width="150"
              :disabled='isMainTld'
              rounded
            >
              <template v-slot:activator="{ on, attrs }">
                <div
                  v-bind="attrs"
                  v-on="on"
                  id='more'
                  class='material-icons-outlined more-btn'
                >
                  more_vert
                </div>
              </template>

              <v-list>
                <v-list-item
                  v-for="(action, i) in menuActions"
                  :key="i"
                  link
                  :disabled="action.value === 'delete' && isSupplyChainOrg"
                  @click='initiateActionFromMenu(action.value)'
                >
                  <v-list-item-icon>
                    <v-icon v-text="action.icon"/>
                  </v-list-item-icon>
                  <v-list-item-title>{{ action.title }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <span class="material-icons-round logo" :class="{ successful: !isVulnerable }" :style="`color: ${successfulIcon.color}`" v-if='!isVulnerable'>
              {{ successfulIcon.iconName }}
            </span>
            <custom-tooltip popper-class="light-tooltip" v-else>
              <div slot='buttonContent'>
                 <span class="material-icons-round logo" :style="`color: ${successfulIcon.color}`"  @click='goToIssues'>
                    {{ successfulIcon.iconName }}
                  </span>
              </div>
              <div slot="content">
                <slot name="tooltip">
                  {{ warningTooltip }}
                </slot>
              </div>
            </custom-tooltip>
            <custom-tooltip class='tool-tip-url' placement='bottom-start' popper-class="light-tooltip">
              <div slot='buttonContent'>
                <div class='url-section'>
                  <div class='url' :class="{ successful: !isVulnerable }">
                    <div class='selected-domain' v-if='isDomainSelected'>
                      <div>{{ title }}</div>
                      <div class='selected-domain__icon'>
                        <v-icon
                          small
                        >
                          radio_button_checked
                        </v-icon>
                      </div>
                    </div>
                    <div v-else>{{ title }}</div>
                  </div>
                  <v-btn  class="open-btn" :href="formatUrl" target="_blank" :ripple="false" @click.stop="">
                    <v-icon>open_in_new</v-icon>
                  </v-btn>
                </div>
              </div>
              <div slot="content">
                <slot name="tooltip">
                  <span class='url-title'>{{ title }}</span>
                </slot>
              </div>
            </custom-tooltip>
            <div :class="isHaveThreeHundredSixtyView? 'configuration-item__degrees-view' : 'configuration-item__degrees-view disabled'" v-if='isForTLD' @click.stop='open360DegreesPopup'>
             <span class='degrees-icon'>
                <degrees-icon />
             </span>
            </div>
          </div>
          <div class='configuration-item__risk-score' v-if='isForTLD' @click.stop='openRiskScorePopup'>
            <risk-score-percentage
              :score='fixedValue'
              :text='score.text'
              :color='score.color'
              :short-form='true'
              :optimized='website.optimized'
              :recalculate-required='website.recalculateRequired'
            />
          </div>
          <div class='configuration-item__validated' v-if='isForTLD'>
            <custom-tooltip  v-if='isDomainValidated' class='tool-tip-validated' placement="bottom" popper-class="light-tooltip">
              <div slot='buttonContent'>
                <div>
                  <span @click.stop='openValidatePopup'>
                    <validated-icon class="validated-icon"/>
                  </span>
                </div>
              </div>
              <div slot="content">
                <slot name="tooltip">
                  {{ $t('domain.validate') }}
                </slot>
              </div>
            </custom-tooltip>
            <custom-tooltip v-else class='tool-tip-validated' placement="bottom" popper-class="light-tooltip">
              <div slot='buttonContent'>
                <div>
                  <span @click.stop='openValidatePopup'>
                    <not-validated-icon class="not-validated-icon"/>
                  </span>
                </div>
              </div>
              <div slot="content">
                <slot name="tooltip">
                  {{ $t('domain.not.validate') }}
                </slot>
              </div>
            </custom-tooltip>
          </div>
          <div class='configuration-item__sensitivity' v-if='isForTLD'>
            <asset-sensitivity
              :sensitivity='domainSensitivity'
              @openSensitivityPopup='openSensitivityPopup'
            />
          </div>
          <div class='configuration-item__waf' v-if='isForTLD'>
            <div class='configuration-item__waf__values'>
              <div class='configuration-item__waf__values__part1'> {{ subdomainsWafData.waf }} </div>
              <w-a-f-half class='configuration-item__waf__half'/>
              <div class='configuration-item__waf__values__part2'> {{  subdomainsWafData.total - subdomainsWafData.waf }} </div>
            </div>
          </div>
          <div class='configuration-item__saas-platforms' v-if='isForTLD'>
            <div class='configuration-item__saas-platforms__icons'
                 v-for='platform in subdomainSaasPlatformsDataKeys'
                 :key='platform'
            >
              <v-img class='configuration-item__saas-platforms__icons__item' :src='subdomainSaasPlatformsIcons[platform]'/>
            </div>
          </div>
          <div class='configuration-item__assets-amount' v-if='isForTLD' @click.stop='openAssetsAmountPopup'>
            <div class='box' :class='[tier]'>
              <div class='configuration-item__assets-amount__section_wrappers'>
                {{ assetsFormatted || 0 }}
              </div>
            </div>
          </div>
          <div class='configuration-item__stats' v-if='isForTLD'>
            <custom-tooltip class='configuration-item__stats__section_wrapper subdomain-data' placement="bottom-start" popper-class="light-tooltip">
              <div slot='buttonContent'>
                <div>
                  <div class='box'>
                    <div class='configuration-item__stats__section_wrapper__icon-separator'>
                      <v-icon>language</v-icon>
                    </div>
                    <div class='configuration-item__stats__section_wrapper__subdomains-stats'>
                      {{ subdomainStats.alive }}/{{ subdomainStats.total }}
                    </div>
                  </div>
                </div>
              </div>
              <div slot="content">
                <slot name="tooltip">
                  <span>{{ $t('tld.tab.stats.subdomains.tooltip') }}</span>
                </slot>
              </div>
            </custom-tooltip>
            <custom-tooltip class='configuration-item__stats__section_wrapper tech-data' placement="bottom-start" popper-class="light-tooltip">
              <div slot='buttonContent'>
                <div>
                  <div class='box'>
                    <div class='configuration-item__stats__section_wrapper__icon-separator'>
                      <v-icon class='material-icons-outlined'>widgets</v-icon>
                    </div>
                    <div class='configuration-item__stats__section_wrapper__technologies-stats'>
                      {{ technologiesStats.vulnerable }}/{{ technologiesStats.total }}
                    </div>
                  </div>
                </div>
              </div>
              <div slot="content">
                <slot name="tooltip">
                  <span>{{ $t('tld.tab.stats.technologies.tooltip') }}</span>
                </slot>
              </div>
            </custom-tooltip>
            <custom-tooltip class='configuration-item__stats__section_wrapper cloud-data' placement="bottom-start" popper-class="light-tooltip">
              <div slot='buttonContent'>
                <div>
                  <div class='box'>
                    <div class='configuration-item__stats__section_wrapper__icon-separator'>
                      <v-icon class='material-icons-outlined'>cloud_queue</v-icon>
                    </div>
                    <div class='configuration-item__stats__section_wrapper__cloud-asset-stats'>
                      {{ cloudAssetsStats.exposed  + bucketsStats.exposed }}/{{ cloudAssetsStats.total  + bucketsStats.total }}
                    </div>
                  </div>
                </div>
              </div>
              <div slot="content">
                <slot name="tooltip">
                  <span>{{ $t('tld.tab.stats.cloud.tooltip') }}</span>
                </slot>
              </div>
            </custom-tooltip>
          </div>
          <div class='configuration-item__last-scanned' v-if='isForTLD'>
            {{ lastScannedDate }}
          </div>
          <div class="configuration-item__actions" :style='`width: ${actionsWidth}px`'>
            <custom-tooltip placement="right" v-if="!isOpen" popper-class="light-tooltip">
              <div slot='buttonContent'>
                <v-btn class="add-test-btn" @click.stop="openAddTestPopup" :ripple="false" :disabled='isAddTestDisabled'>
                  <v-icon class="add-test-icon-no-txt">add</v-icon>
                </v-btn>
              </div>
              <div slot="content">
                <slot name="tooltip">
                  <span> {{ toolTipText }}</span>
                </slot>
              </div>
            </custom-tooltip>
          </div>
          <div class='configuration-item__tld' v-if='isManual'>
            <custom-tooltip placement="right" popper-class="light-tooltip">
              <div slot='buttonContent'>
                <v-btn v-if='isManual' class="edit-tld-btn" :ripple="false" @click.stop="showPopup = true">
                  <v-icon>border_color</v-icon>
                </v-btn>
              </div>
              <div slot="content">
                <slot name="tooltip">
                  <span>edit</span>
                </slot>
              </div>
            </custom-tooltip>
          </div>
          <slot name="control" v-if="!isOpen" />
          <v-icon class="expand-icon" :class="{ expand: isOpen }">expand_more</v-icon>
        </div>
        <div class="test-container" v-if="isOpen">
          <test-card v-for="(testCard, key) in testCardsForDisplay" :test-card="testCard" :item="title" :key="key" />
          <div v-if='!isAddTestDisabled' class="add-test-area" v-ripple="{ class: `blue--text` }" @click="openAddTestPopup">
            <v-icon class="add-test-icon">add</v-icon>
            <span class="add-test-btn-txt">{{ $t('configuration.add.test') }}</span>
          </div>
          <div class='test-card-display-toggle' v-if='fullTestCardsFormatted.length > amountOfTestCardInRow'>
            <span text class='toggle-test-card-btn' @click.stop="showAllTestCards = !showAllTestCards">
              {{ displayTestCardsButtonLabel }}
            </span>
          </div>
        </div>
        <EditTldPopup
          v-model="showPopup"
          :visiblePopup="showPopup"
          title="configuration.edit.tld"
          :save-button-title="$t('configuration.save')"
          :currentSelectedTld='currentSelectedTld'
          :item="website"
          @changed="editTld"
          @cancel='showPopup = false'
        />
        <add-test-popup
          v-model="addTestPopup"
          :website="title"
          :tab="'configuration'"
          :class='title'
          :tld='tld'
          :organization='website.organization || {}'
          :testTypes="testTypes"
          @showFeatureTogglePopup='showFeatureTogglePopup'
          @submit="$emit('add-test', { test: $event })"
        />
      </div>
      <div class='configuration-item__content__details'>
        <div class='configuration-item__content__details__organization'>
          <v-chip
            label
            outlined
            pill
            text-color='#657D94'
            small
            class='tag'
          >
             <span class="material-icons-outlined">
                business
            </span>
            <span class='organization-name'>
              {{organization}}
            </span>
          </v-chip>
        </div>
        <div class='configuration-item__content__details__tags'>
          <div class='configuration-item__content__details__tags__tags-list'>
            <v-chip
              :ripple='false'
              label
              outlined
              close
              pill
              text-color='#2459d5'
              small
              class='tag'
              v-for='(tagKey, index) in nonExcludedTagKeys'
              :key='index'
              close-icon='close'
              @click:close='removeTagFromList(tagKey)'
              @click.stop='addTagFilter(tagKey)'
            >
              {{ tagKey }}
            </v-chip>
          </div>
          <span class='no-tags' v-if='nonExcludedTagKeys.length === 0'>
          {{ $t('no.tags') }}
        </span>
          <span class="material-icons" @click='openAddTagPopup'>
          local_offer
        </span>
        </div>
      </div>
    </div>
    <YesNoPopup
      title='cloud.services.disabled.popup.header'
      :visible-popup='isShowFeatureTogglePopup'
      :showNoButton='false'
      yes-caption='cloud.services.disabled.popup.ok'
      message='cloud.services.disabled.popup.message'
      @clicked='isShowFeatureTogglePopup = false'
    />
    <assets-count-details-popup
      :tier='tier'
      :visible-popup='showAssetsAmountPopup'
      :domains='domainForAssetsPopup'
      :stats="statsForAssetsPopup"
      @clicked='openAssetsAmountPopup'
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { CONFIGURATION, CONFIGURATION_ACTION_TYPES } from '@/constants/configuration';
import CustomCheckbox from '@/components/common/CustomCheckbox.vue';
import TestCard from '@/components/configuration/TestCard.vue';
import AddTestPopup from '@/components/popup/AddTestPopup.vue';
import AwsIcon from '@/assets/aws.svg';
import GcpIcon from '@/assets/gcp.svg';
import AzureIcon from '@/assets/azure.svg';
import WAFHalf from '@/assets/icons/waf-half.svg?inline';
import EditTldPopup from '@/components/popup/EditTldPopup.vue';
import AssetSensitivity from '@/components/common/AssetSensitivity.vue';
import RiskScorePercentage from '@/components/common/RiskScorePercentage.vue';
import riskScoreLetters from '@/constants/riskScore';
import validatedIcon from '@/assets/icons/validated.svg?inline';
import degreesIcon from '@/assets/icons/dergrees.svg?inline';
import notValidatedIcon from '@/assets/icons/not-validated.svg?inline';
import YesNoPopup from '@/components/popup/YesNoPopup.vue';
import { views } from '@/router/constants';
import { SET_DOMAIN_FILTER } from '@/store/filters/actionTypes';
import {tierColors} from '@/constants/game';
import AssetsCountDetailsPopup from '@/components/popup/AssetsCountDetailsPopup.vue';
import CustomTooltip from '../common/CustomTooltip.vue';

export default {
  name: 'ConfigurationItemRow',
  props: {
    actionsWidth: {
      type: Number,
      default: 1120,
      required: false,
    },
    currentSelectedTld: {
      type: String,
      default: null,
    },
    isManual: {
      type: Boolean,
      default: () => false,
    },
    isMainTld: {
      type: Boolean,
      default: () => false,
    },
    isSupplyChainOrg: {
      type: Boolean,
      default: () => false,
    },
    isSelected: {
      type: Boolean,
      default: () => false,
    },
    riskScoreValue: {
      type: Number,
      required: false,
      default: 100,
    },
    showOpenLink: {
      type: Boolean,
      default: false,
    },
    website: {},
    warningTooltip: {
      type: String,
    },
    testTypes: {},
    tab: {},
    amountOfTestCardInRow: {
      type: Number,
      default: 5,
      required: false,
    },
    lastScannedDate: {
      required: false,
    },
    assetsAmount: {
      type: Number,
      required: false,
    },
  },
  components: {
    notValidatedIcon,
    RiskScorePercentage,
    AssetSensitivity,
    CustomTooltip,
    CustomCheckbox,
    TestCard,
    AddTestPopup,
    EditTldPopup,
    validatedIcon,
    degreesIcon,
    WAFHalf,
    YesNoPopup,
    AssetsCountDetailsPopup,
  },
  data() {
    return {
      isOpen: false,
      addTestPopup: false,
      showPopup: false,
      showAllTestCards: false,
      isShowFeatureTogglePopup: false,
      windowInnerWidth: window.innerWidth,
      showAssetsAmountPopup: false,
    };
  },
  watch: {
    title() {
      this.isOpen = false;
    },
  },
  created() {
    window.addEventListener('resize', this.windowResizeHandler);
  },
  destroyed() {
    window.removeEventListener('resize', this.windowResizeHandler);
  },
  methods: {
    async openAssetsAmountPopup() {
      this.showAssetsAmountPopup = !this.showAssetsAmountPopup;
    },
    openAddTestPopup() {
      if (!this.isAddTestDisabled) {
        this.addTestPopup = true;
      }
    },
    showFeatureTogglePopup() {
      this.isShowFeatureTogglePopup = true;
    },
    async initiateActionFromMenu(action) {
      if (action === CONFIGURATION.TLD_ACTIONS_TYPES.delete) {
        if (!this.isMainTld && !this.isSupplyChainOrg) {
          await this.deleteTld();
        }
      } else if (action === CONFIGURATION.TLD_ACTIONS_TYPES.settings) {
        await this.domainScanSettings();
      }
    },
    async deleteTld() {
      await this.$emit('openDeleteTldPopup', this.title);
    },
    async domainScanSettings() {
      await this.$emit('openScanSettingsPopup', {tld: this.title, website: this.website, scanSettings: {...this.website.scanSettings}});
    },
    async goToIssues() {
      const prevTitle = this.title;
      await this.$store.dispatch(SET_DOMAIN_FILTER, [this.title]);
      await this.$router.push({ name: views.ISSUES, params: { search: prevTitle }});
    },
    openAddTagPopup() {
      this.$emit('openTagsPopup', [this.website.configData]);
    },
    addTagFilter(tag) {
      this.$emit('addTagFilter', tag);
    },
    async removeTagFromList(tag) {
      const tagsUpdated = {...this.tags};
      delete tagsUpdated[tag];

      await this.$emit('submitTagsInformation', {
        action: CONFIGURATION_ACTION_TYPES.REMOVE_TAG,
        configType: CONFIGURATION.CONFIG_TYPES.TAGS_DOMAIN,
        items: [{
          ...this.website,
          url: this.title,
          tld: this.title,
          tags: { ...tagsUpdated },
        }],
      });
    },
    async openRiskScorePopup() {
      await this.$emit('openRiskScorePopup', { website: this.website, score: this.score, fixedValue: this.riskScoreValue });
    },
    async openSensitivityPopup() {
      await this.$emit('openSensitivityPopup', this.website);
    },
    openValidatePopup() {
      this.$emit('openValidatePopup', this.website);
    },
    async open360DegreesPopup() {
      if (this.isHaveThreeHundredSixtyView) {
        this.$emit('open360DegreesPopup', this.website);
      }
    },
    async editTld(selectedDomain) {
      await this.$emit('edit', { tld: selectedDomain, website: this.website });
    },
    windowResizeHandler() {
      this.windowInnerWidth = window.innerWidth;
    },
    isSaasPlatformExistForType(saasType) {
      return (this.websiteStatistics?.cloudAssets?.[saasType]?.exposed || 0) +
        (this.websiteStatistics?.cloudAssets?.[saasType]?.private || 0) > 0 ||
        (this.websiteStatistics?.buckets?.[saasType]?.exposed || 0) +
        (this.websiteStatistics?.buckets?.[saasType]?.private || 0) > 0;
    },
  },
  computed: {
    ...mapGetters([
      'testCardsSubdomains',
      'testCardsHosts',
      'testCardsDomains',
      'domainsTags',
      'organizationsTags',
      'domainFilter',
      'amountOfRunningProjects',
      'licenses',
      'domains',
    ]),
    domainForAssetsPopup() {
      return [this.website?.configData] || [];
    },
    statsForAssetsPopup() {
      return this.website?.statistics?.data || {};
    },
    assetsNumericValue() {
      if (this.assetsAmount) {
        return parseInt(this.assetsAmount, 10);
      }
      return 0;
    },
    assetsFormatted() {
      return this.assetsNumericValue > 9999 ? '9,999+' : this.assetsNumericValue.toLocaleString('en-US');
    },
    tier() {
      if (this.assetsNumericValue < 31) return tierColors.blue;
      if (this.assetsNumericValue < 91) return tierColors.bronze;
      if (this.assetsNumericValue < 251) return tierColors.silver;
      if (this.assetsNumericValue < 1001) return tierColors.gold;
      if (this.assetsNumericValue < 2501) return tierColors.platinum;
      return tierColors.purple;
    },
    organization() {
      return this.website.organization?.name || '';
    },
    isAddTestDisabled() {
      return !this.licenses[this.organization]?.length && this.amountOfRunningProjects[this.organization] > 2;
    },
    toolTipText() {
      return this.isAddTestDisabled ? this.$t('license.disable.above.3.scans') : this.$t('configuration.add.test');
    },
    isDomainSelected() {
      return this.domainFilter.includes(this.title);
    },
    menuActions() {
      return Object.values(CONFIGURATION.TLD_ACTIONS_TYPES).map(item => ({ value: item, title: this.$t(`tld.action.${item}`), icon: CONFIGURATION.TLD_ACTIONS_TYPE_ICONS[item] }));
    },
    successfulIcon() {
      if (this.isVulnerable) {
        return { iconName: 'warning', color: '#E0255E' };
      }
      return { iconName: 'check_circle', color: '#2baf69' };
    },
    isVulnerable() {
      return this.website.vulnerable;
    },
    tagKeys() {
      return Object.keys(this.tags);
    },
    nonExcludedTagKeys() {
      return this.tagKeys.filter(key => this.tags[key] !== 'exclude');
    },
    tags() {
      return this.website?.tags || {};
    },
    subdomainStats() {
      return this.formattedStatsData.subdomainsStats || { total: 0, alive: 0 };
    },
    technologiesStats() {
      return this.formattedStatsData.technologiesStats || { total: 0, vulnerable: 0 };
    },
    cloudAssetsStats() {
      return this.formattedStatsData.cloudAssetsStats || { total: 0, exposed: 0 };
    },
    bucketsStats() {
      return this.formattedStatsData.bucketsStats || { total: 0, exposed: 0 };
    },
    formattedStatsData() {
      return {
        subdomainsStats: { total: this.websiteStatistics.subdomains?.total || 0, alive: this.websiteStatistics.subdomains?.alive || 0 },
        technologiesStats: { total: this.websiteStatistics.technologies?.summary?.total || 0, vulnerable: this.websiteStatistics.technologies?.summary?.vulnerable || 0 },
        cloudAssetsStats: { total: this.websiteStatistics.cloudAssets?.summary?.total || 0, exposed: this.websiteStatistics.cloudAssets?.summary?.total_exposed || 0 },
        bucketsStats: { total: this.websiteStatistics.buckets?.summary?.total || 0, exposed: this.websiteStatistics.buckets?.summary?.total_exposed || 0 },
      };
    },
    subdomainSaasPlatformsData() {
      return {
        aws: this.isSaasPlatformExistForType('aws') || false,
        azure: this.isSaasPlatformExistForType('azure') || false,
        gcp: this.isSaasPlatformExistForType('gcp') || false,
      };
    },
    subdomainSaasPlatformsIcons() {
      return {
        aws: AwsIcon,
        gcp: GcpIcon,
        azure: AzureIcon,
      };
    },
    subdomainSaasPlatformsDataKeys() {
      return Object.keys(this.subdomainSaasPlatformsData).filter(key => this.subdomainSaasPlatformsData[key]) || [];
    },
    subdomainsWafData() {
      return {
        waf: this.websiteStatistics?.subdomains?.waf || 0,
        total: this.websiteStatistics?.subdomains?.alive || 0,
      };
    },
    websiteStatistics() {
      return this.website?.statistics?.data || {};
    },
    riskScoreLetters() {
      return riskScoreLetters;
    },
    score() {
      const result = this.riskScoreLetters[this.index].letter;
      const {color} = this.riskScoreLetters[this.index];
      return {
        text: result,
        color,
      };
    },
    index() {
      const localValue = 100 - this.riskScoreValue;
      if (localValue >= 95) {
        return 0;
      }
      if (localValue >= 81) {
        return 1;
      }
      if (localValue >= 71) {
        return 2;
      }
      if (localValue >= 61) {
        return 3;
      }
      if (localValue >= 51) {
        return 4;
      }
      return 5;
    },
    fixedValue() {
      return (100 - this.riskScoreValue).toFixed(1);
    },
    testCards() {
      if (this.tab === CONFIGURATION.TAB.subdomains) {
        return this.testCardsSubdomains || [];
      }
      if (this.tab === CONFIGURATION.TAB.hosts) {
        return this.testCardsHosts || [];
      }
      if (this.tab === CONFIGURATION.TAB.domains) {
        return this.testCardsDomains || [];
      }

      if (this.tab === CONFIGURATION.TAB.infrastructure) {
        return this.testCardsInfrastructure || [];
      }
      return [];
    },
    isForTLD() {
      return this.tab === CONFIGURATION.TAB.domains;
    },
    fullTestCardsFormatted() {
      const cards = this.testCards[this.title] || [];
      // Most-recently-updated first, so a retried scan (its project.updatedDate is
      // bumped on retry) jumps to the front of the inventory.
      const recency = c => new Date(
        c?.project?.updatedDate || c?.project?.startDate || c?.startDate || 0,
      ).getTime();
      return [...cards].sort((a, b) => recency(b) - recency(a));
    },
    title() {
      return this.website?.configData || this.website;
    },
    tld() {
      return this.website.tld || this.website.configData;
    },
    testCardsForDisplay() {
      const maxPossibleTestCards = this.fullTestCardsFormatted.length < this.amountOfTestCardInRow ? this.fullTestCardsFormatted.length : this.amountOfTestCardInRow;
      return this.showAllTestCards ? this.fullTestCardsFormatted : this.fullTestCardsFormatted.slice(0, maxPossibleTestCards);
    },
    displayTestCardsButtonLabel() {
      return this.showAllTestCards ? this.$t('hide.test.cards') : this.$t('show.all.test.cards');
    },
    formatUrl() {
      const url = this.title;
      return `http://${url}`;
    },
    isDomainValidated() {
      return this.website.validated === true;
    },
    isHaveThreeHundredSixtyView() {
      return (this.website?.threeHundredSixty !== 'undefined' && this.website.threeHundredSixty === true);
    },
    domainSensitivity() {
      return this.website.sensitivity || CONFIGURATION.SensitivityOptions.HIGH;
    },
  },
};
</script>

<style scoped lang="scss">
@import "src/assets/styles/variables";
.theme--light.v-list {
  width: 170px !important;
}
.v-list-item {
  &__icon {
    margin-right: 15px !important;
    margin-top: 10px !important;
    margin-bottom: 10px !important;
  }
  min-height: 30px;
  padding-right: 15px !important;
  padding-left: 15px !important;
}

.list-group-title {
  padding-left: 15px;
}

.list-sub-group-item {
  padding-left: 15px !important;
}

.v-list-item__title {
  font-family: $default-application-font-medium;
  font-size: 12px;
}
.configuration-item {
  .v-chip {
    background-color: var(--bg-tertiary) !important;
    border-color: transparent !important;
    padding: 10px !important;
  }
}
</style>

<style scoped lang="scss">
@import "src/assets/styles/variables";
.configuration-item {
  &__assets-amount {
    font-family: $default-application-font-semi-bold !important;
    color: #FFFFFF;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0;
    display: flex;
    flex-direction: row;
    .box{
      width: 63px;
      display: flex;
      flex-direction: row;
      padding: 3px 3px;
      border-radius: 3px;
      justify-content: center;
      background-image: $purple-gradient;
      &.blue {
        background-image: $blue-gradient;
      }
      &.bronze {
        background-image: $bronze-gradient;
      }
      &.silver {
        background-image: $silver-gradient;
      }
      &.gold {
        background-image: $gold-gradient;
      }
      &.platinum {
        background-image: $platinum-gradient;
      }
    }
  }
  &__stats{
    font-family: $default-application-font-semi-bold !important;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0;
    display: flex;
    .subdomain-data {
      margin-left: 3.5%;
    }
    .tech-data {
      margin-left: 14%;
    }
    .cloud-data {
      margin-left: 4%;
    }
    &__section_wrapper {
      display: flex;
      flex-direction: row;
      width: 33%;
      .box{
        background-color: var(--bg-tertiary);
        border: 1px solid var(--border-primary);
        display: flex;
        flex-direction: row;
        padding: 6px 3px;
        border-radius: 3px;
        text-align: left;
      }
      &__subdomains-stats, &__technologies-stats, &__cloud-asset-stats {
        font-size: 16px;
        color: var(--text-primary);
      }
      &__icon-separator {
        margin-right: 5px;
        .v-icon {
          color: var(--text-tertiary) !important;
          font-size: 18px;
          font-weight: 400;
        }
      }
    }
  }
  &__waf {
    display: flex;
    align-items: center;
    width: 7%;
    align-content: center;
    justify-content: center;
    &__values {
      font-family: $default-application-font-semi-bold !important;
      font-size: 16px;
      font-weight: 600;
      letter-spacing: 0;
      display: flex;
      &__part1{
        color: var(--severity-low);
        width: 40px;
        display: flex;
        justify-content: right;
      }
      &__part2{
        color: var(--text-primary);
        width: 40px;
      }
    }
    &__half{
      width: 22px;
      fill: white;
      margin-right: 5px;
      margin-left: 5px;
    }
  }
}
#app .configuration-item {
  border-bottom: 1px solid var(--border-primary);
  display: flex;
  padding: 5px 0;
  &__tld {
    width: 80px;
  }
  &__validated {
    display: flex;
    width: 6.5%;
    flex-shrink: 0;
    .validated-icon {
      z-index: 5;
      fill: white;
      .recolor.recolor {
        fill: #5ca8ed;
      }
      width: 28px;
      &:hover {
        cursor: pointer;
      }
    }
    .not-validated-icon {
      z-index: 1;
      fill: white;
      .recolor.recolor {
        fill: #9fa5b4;
      }
      width: 28px;
      &:hover {
        cursor: pointer;
      }
    }
    .tool-tip-validated {
      width: 130px;
      margin-left: -47px;
    }
  }
  &__risk-score {
    width: 8%;
    flex-shrink: 0;
    align-self: center;
    display: flex;
    &:hover {
      cursor: pointer;
    }
  }
  &__degrees-view {
    margin-left: 20px;
    align-self: center;
    min-width: 40px;
    max-width: 40px;
    min-height: 30px;
    max-height: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: $good-button-background-color;
    border-radius: 3px;
    &.disabled {
      background-color: var(--bg-tertiary);
      &:hover {
        background-color: var(--bg-tertiary);
        border: none;
      }
      .degrees-icon {
        fill: var(--text-muted);
        &:hover {
          cursor: default;
        }
      }
    }
    &:hover {
      background-color: $good-button-hover-background-color;
      border: $good-button-hover-border;
    }
    &:active{
      border: $good-button-active-border;
      background-color: $good-button-active-background-color;
    }
    .degrees-icon{
      width: 28px;
      height: 25px;
      fill: $good-button-text-color;
      &:hover {
        cursor: pointer;
      }
    }
  }
  &__sensitivity {
    align-self: center;
    display: flex;
    width: 6%;
    flex-shrink: 0;
  }
  &__stats {
    display: flex;
    width: 20%;
    align-items: center;
    &__icon-separator{
      color: #01cfc5;
    }
  }
  &__assets-amount {
    width: 5%;
    flex-shrink: 0;
  }
  &__last-scanned {
    width: 9%;
    flex-shrink: 0;
    padding-left: 2.39%;
  }
  &__saas-platforms {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 8.5%;
    flex-shrink: 0;
    &__icons {
      width: 25px;
      margin-right: 10px;
    }
  }
  &__actions {
    flex-direction: row;
    display: flex;
    width: 8% !important;
    justify-content:flex-end;
    &__delete-tld {
      &:hover {
        cursor: pointer;
        border: $good-button-hover-border;
        background-color: $good-button-hover-background-color !important;
      }
      &:active{
        border: $good-button-active-border;
        background-color: $good-button-hover-background-color !important;
      }
      .delete-animation{
        height: 30px;
        margin-top: -5px;
      }
    }
  }
  &__open{
    padding-top: 2px;
    z-index: 4;
  }

  &__control {
    margin-top: 7px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    &__info {
      z-index: 4;
      width: 100%;
      .v-btn:not(.v-btn--round).v-size--default{
        padding: 10px;
      }
      .add-test-btn-txt {
        background-color: transparent !important;
        box-shadow: none;
        color: var(--brand-primary);
        font-family: $default-application-font-medium;
        font-size: 13px;
        letter-spacing: 1.1px;
        line-height: 16px;
        text-transform: uppercase;
      }
      .add-test-btn.add-test-btn {
        background-color: var(--btn-action-bg);
        text-align: center;
        box-shadow: none;
        min-width: 28px;
        max-width: 28px;
        min-height: 28px;
        max-height: 28px;
        user-select: none;
        color: var(--btn-action-border-color);
        .v-icon {
          font-size: 18px;
          color: var(--btn-action-border-color);
        }
        &:hover {
          cursor: pointer;
          border: $action-button-hover-border;
          background-color: var(--btn-action-hover-bg);
        }
        &:active{
          border: $action-button-active-border;
          background-color: var(--btn-action-hover-bg);
        }
        &:before{
          color: transparent;
        }

      }
      .edit-tld-btn {
        background-color: var(--brand-primary) !important;
        box-shadow: none;
        font-family: $default-application-font-medium;
        text-align: center;
        min-width: 28px;
        max-width: 28px;
        min-height: 28px;
        max-height: 28px;
        color: var(--text-inverse);
        margin-top: 2px;
        .v-icon {
          font-size: 18px;
        }
        &:hover {
          cursor: pointer;
          border: $action-button-hover-border;
          background-color: var(--brand-primary-hover) !important;
        }
        &:active{
          border: $action-button-active-border;
          background-color: var(--brand-primary-hover) !important;
        }
        &:before{
          color: transparent;
        }
      }
      .open-btn {
        background-color: $action-button-background-color;
        text-align: center;
        box-shadow: none;
        min-width: 28px;
        max-width: 28px;
        min-height: 28px;
        max-height: 28px;
        user-select: none;
        color: var(--brand-secondary);
        .v-icon {
          font-size: 18px;
          color: var(--brand-secondary);
        }
        &:hover {
          cursor: pointer;
          border: $action-button-hover-border;
          background-color: $action-button-hover-background-color !important;
        }
        &:active{
          border: $action-button-active-border;
          background-color: $action-button-hover-background-color !important;
        }
        &:before{
          color: transparent;
        }
      }

      .add-test-icon {
        color: var(--brand-primary);
        font-size: 22px;
        margin-right: 3px;
        &-no-txt{
          margin-right: 0;
        }
      }

      .main-info {
        align-items: center;
        display: flex;
        width: 100%;
        &:hover {
          cursor: pointer;
        }
      }

      .url-title {
        width: 500px !important;
      }

      .site {
        align-items: center;
        color: var(--text-primary);
        cursor: pointer;
        display: flex;
        width: 24.7%;
        flex-shrink: 0;
        padding-right: 3%;
        font-family: $default-application-font-medium;
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
        height: 32px;

        .more-btn {
          opacity: 0;
          color: var(--text-muted);
          margin-top: -6px;
          &:hover {
            cursor: pointer;
          }
        }

        .material-icons-round {
          margin-bottom: 6px;
          margin-right: 4px;
          &.successful {
            &:hover {
              cursor: auto !important;
            }
          }
          &:hover {
            cursor: pointer;
            color: $default-button-hover-color;
          }
        }

        .tool-tip-url {
          width: 100%;
          margin-left: 0;
          overflow: hidden;
          background-color: transparent;
        }

        .url-section {
          display: flex;
          width: 100%;
          justify-content: space-between;
          .url {
            &.successful {
              font-weight: 500;
            }
            width: 75%;
            display: flex;
            overflow: hidden;
            margin-top: 5px;
            color: var(--text-primary);
            font-weight: 600;
            > div {
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            .selected-domain{
              display: flex;
              flex-direction: row;
              justify-content: space-between;
              overflow: hidden;
              color: var(--brand-secondary);
              font-weight: 600;
              // Ensure the domain name text is purple
              div {
                color: var(--brand-secondary);
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              }
              &__icon{
                position: absolute;
                left: 17%;
                .v-icon{
                  -webkit-text-stroke-width: 0.5px;
                  -webkit-text-stroke-color: var(--brand-secondary);
                  -webkit-text-fill-color: var(--brand-secondary);
                }
              }
            }
          }
        }
        &.successful {
          font-weight: 500;
        }
        .status-icon {
          margin-right: 10px;
          margin-bottom: 6px;
        }
      }
    }
    &__details {
      width: 100%;
      margin-top: 18px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      &__organization {
        display: flex;
        align-self: start;
        justify-self: start;
        align-items: center;
        .organization-name {
          text-transform: capitalize;
          font-size: 10px !important;
        }
        .material-icons-outlined {
          color: $default-button-color;
          font-size: 14px;
          margin-right: 3px;
        }
      }
      &__tags {
        display: flex;
        align-items: center;
        margin-right: 10px;
        align-self: end;
        justify-self: end;
        &__tags-list {
          margin-right: 10px;
          .tag {
            margin-right: 8px;
          }
        }
        .material-icons {
          color: $default-button-color;
          font-size: 18px;
          &:hover {
            cursor: pointer;
            color: $default-button-hover-color;
          }
        }
        .no-tags {
          margin-right: 5px;
          color:$widget-legend-color;
          font-family: $default-application-font-regular;
          font-size: 12px;
          letter-spacing: 1.1px;
          line-height: 15px;
        }
      }
    }
  }
  .expand-icon {
    margin-left: auto;
  }
}

#row:hover #more {
  opacity: 1;
}
</style>
